import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form,
  Input,
  Button,
  Switch, 
  Modal, 
  Space, 
  Typography, 
  Alert,
  Divider,
  List,
  message,
  Row,
  Col,
  Avatar
} from 'antd';
import { 
  UserOutlined,
  SafetyOutlined, 
  LockOutlined,
  ReloadOutlined,
  DownloadOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import { authApi } from '../../api/auth';
import { getCurrentUser } from '../../store/slices/authSlice';
import MFASetup from '../../components/auth/MFA/MFASetup';

const { Title, Text, Paragraph } = Typography;

const Profile: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<AppDispatch>();
  
  const [mfaStatus, setMfaStatus] = useState({
    mfa_enabled: false,
    backup_tokens_count: 0,
    failed_attempts: 0,
    is_rate_limited: false
  });
  
  const [loading, setLoading] = useState(false);
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showDisableMFA, setShowDisableMFA] = useState(false);
  const [showRegenerate, setShowRegenerate] = useState(false);
  
  const [passwordForm] = Form.useForm();
  const [disableMFAForm] = Form.useForm();
  const [regenerateForm] = Form.useForm();

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      const status = await authApi.mfaStatus();
      setMfaStatus(status);
    } catch (error) {
      console.error('Error fetching MFA status:', error);
    }
  };

  const handlePasswordChange = async (values: any) => {
    setLoading(true);
    try {
      await authApi.changePassword(values);
      message.success('Password changed successfully');
      setShowPasswordChange(false);
      passwordForm.resetFields();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Error changing password');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMFA = async (values: any) => {
    setLoading(true);
    try {
      await authApi.mfaDisable(values);
      message.success('MFA disabled successfully');
      setShowDisableMFA(false);
      disableMFAForm.resetFields();
      fetchMFAStatus();
      dispatch(getCurrentUser());
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Error disabling MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupTokens = async (values: any) => {
    setLoading(true);
    try {
      const response = await authApi.mfaRegenerateBackupTokens(values);
      
      // Download new backup tokens
      const content = `TinsaFlow MFA Backup Codes\n\nGenerated: ${new Date().toISOString()}\n\n${response.backup_tokens.join('\n')}\n\nStore these codes in a safe place. Each code can only be used once.`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tinsaflow-backup-codes-new.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      message.success('Backup tokens regenerated successfully');
      setShowRegenerate(false);
      regenerateForm.resetFields();
      fetchMFAStatus();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Error regenerating backup tokens');
    } finally {
      setLoading(false);
    }
  };

  if (showMFASetup) {
    return (
      <div style={{ padding: 24 }}>
        <Button 
          onClick={() => setShowMFASetup(false)}
          style={{ marginBottom: 16 }}
        >
          ← Back to Profile
        </Button>
        <MFASetup onMFAStatusChange={() => {
          setShowMFASetup(false);
          fetchMFAStatus();
          dispatch(getCurrentUser());
        }} />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <Title level={2}>Mon Profil</Title>
      
      {/* User Information Card */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={24} align="middle">
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Avatar size={100} icon={<UserOutlined />} />
              <div style={{ marginTop: 16 }}>
                <Title level={4} style={{ margin: 0 }}>
                  {user?.first_name} {user?.last_name}
                </Title>
                <Text type="secondary">{user?.email}</Text>
              </div>
            </div>
          </Col>
          <Col span={18}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text type="secondary">Rôle:</Text>
                <div style={{ fontWeight: 500, textTransform: 'capitalize' }}>
                  {user?.role}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Téléphone:</Text>
                <div style={{ fontWeight: 500 }}>
                  {user?.telephone || 'Non renseigné'}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Département:</Text>
                <div style={{ fontWeight: 500 }}>
                  {user?.departement_nom || 'Non renseigné'}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary">Membre depuis:</Text>
                <div style={{ fontWeight: 500 }}>
                  {user?.date_creation ? new Date(user.date_creation).toLocaleDateString('fr-FR') : 'N/A'}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      {/* Security Settings */}
      <Card title="Sécurité" style={{ marginBottom: 24 }}>
        
        {/* Password Section */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={5} style={{ margin: 0 }}>
                <LockOutlined style={{ marginRight: 8 }} />
                Mot de passe
              </Title>
              <Text type="secondary">
                Changez votre mot de passe pour sécuriser votre compte
              </Text>
            </div>
            <Button 
              icon={<EditOutlined />}
              onClick={() => setShowPasswordChange(true)}
            >
              Changer le mot de passe
            </Button>
          </div>
        </div>

        <Divider />

        {/* MFA Section */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Title level={5} style={{ margin: 0 }}>
                <SafetyOutlined style={{ 
                  marginRight: 8, 
                  color: mfaStatus.mfa_enabled ? '#52c41a' : '#8c8c8c' 
                }} />
                Authentification à deux facteurs (2FA)
              </Title>
              <Text type="secondary">
                {mfaStatus.mfa_enabled 
                  ? 'Votre compte est protégé par 2FA'
                  : 'Ajoutez une couche de sécurité supplémentaire'
                }
              </Text>
            </div>
            <Switch 
              checked={mfaStatus.mfa_enabled}
              onChange={(checked) => {
                if (checked) {
                  setShowMFASetup(true);
                } else {
                  setShowDisableMFA(true);
                }
              }}
              disabled={loading}
            />
          </div>

          {mfaStatus.mfa_enabled && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <div>
                <Title level={5}>Codes de récupération</Title>
                <Paragraph>
                  Vous avez {mfaStatus.backup_tokens_count} codes de récupération restants. 
                  Ces codes peuvent être utilisés pour accéder à votre compte si vous perdez votre appareil d'authentification.
                </Paragraph>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={() => setShowRegenerate(true)}
                >
                  Régénérer les codes de récupération
                </Button>
              </div>

              {mfaStatus.failed_attempts > 0 && (
                <>
                  <Divider style={{ margin: '16px 0' }} />
                  <Alert
                    message={`${mfaStatus.failed_attempts} tentative(s) d'authentification échouée(s)`}
                    description={
                      mfaStatus.is_rate_limited 
                        ? 'Compte temporairement verrouillé en raison de multiples tentatives échouées.' 
                        : 'Tentatives d\'authentification récentes détectées.'
                    }
                    type={mfaStatus.is_rate_limited ? 'error' : 'warning'}
                    showIcon
                  />
                </>
              )}
            </>
          )}
        </div>
      </Card>

      {/* Change Password Modal */}
      <Modal
        title="Changer le mot de passe"
        open={showPasswordChange}
        onCancel={() => {
          setShowPasswordChange(false);
          passwordForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordChange}
        >
          <Form.Item
            name="current_password"
            label="Mot de passe actuel"
            rules={[{ required: true, message: 'Veuillez saisir votre mot de passe actuel' }]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="new_password"
            label="Nouveau mot de passe"
            rules={[
              { required: true, message: 'Veuillez saisir un nouveau mot de passe' },
              { min: 8, message: 'Le mot de passe doit contenir au moins 8 caractères' }
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="Confirmer le nouveau mot de passe"
            dependencies={['new_password']}
            rules={[
              { required: true, message: 'Veuillez confirmer votre nouveau mot de passe' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Les mots de passe ne correspondent pas'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setShowPasswordChange(false)}>
                Annuler
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Changer le mot de passe
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Disable MFA Modal */}
      <Modal
        title="Désactiver l'authentification à deux facteurs"
        open={showDisableMFA}
        onCancel={() => {
          setShowDisableMFA(false);
          disableMFAForm.resetFields();
        }}
        footer={null}
      >
        <Alert
          message="Attention"
          description="La désactivation de l'authentification à deux facteurs rendra votre compte moins sécurisé."
          type="warning"
          style={{ marginBottom: 16 }}
        />
        
        <Form
          form={disableMFAForm}
          layout="vertical"
          onFinish={handleDisableMFA}
        >
          <Form.Item
            name="otp_token"
            label="Code de votre application d'authentification"
          >
            <Input
              placeholder="000000"
              maxLength={6}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                disableMFAForm.setFieldsValue({ otp_token: value, backup_token: undefined });
              }}
            />
          </Form.Item>
          
          <div style={{ textAlign: 'center', margin: '8px 0' }}>
            <Text type="secondary">ou</Text>
          </div>
          
          <Form.Item
            name="backup_token"
            label="Code de récupération"
          >
            <Input
              placeholder="XXXXXXXX"
              maxLength={8}
              onChange={(e) => {
                const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                disableMFAForm.setFieldsValue({ backup_token: value, otp_token: undefined });
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setShowDisableMFA(false)}>
                Annuler
              </Button>
              <Button type="primary" danger htmlType="submit" loading={loading}>
                Désactiver 2FA
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Regenerate Backup Tokens Modal */}
      <Modal
        title="Régénérer les codes de récupération"
        open={showRegenerate}
        onCancel={() => {
          setShowRegenerate(false);
          regenerateForm.resetFields();
        }}
        footer={null}
      >
        <Alert
          message="Générer de nouveaux codes de récupération"
          description="Cela invalidera tous vos codes de récupération existants et en générera de nouveaux. Assurez-vous de télécharger et de stocker les nouveaux codes en sécurité."
          type="info"
          style={{ marginBottom: 16 }}
        />
        
        <Form
          form={regenerateForm}
          layout="vertical"
          onFinish={handleRegenerateBackupTokens}
        >
          <Form.Item
            name="otp_token"
            label="Code de votre application d'authentification"
          >
            <Input
              placeholder="000000"
              maxLength={6}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                regenerateForm.setFieldsValue({ otp_token: value, backup_token: undefined });
              }}
            />
          </Form.Item>
          
          <div style={{ textAlign: 'center', margin: '8px 0' }}>
            <Text type="secondary">ou</Text>
          </div>
          
          <Form.Item
            name="backup_token"
            label="Code de récupération"
          >
            <Input
              placeholder="XXXXXXXX"
              maxLength={8}
              onChange={(e) => {
                const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                regenerateForm.setFieldsValue({ backup_token: value, otp_token: undefined });
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setShowRegenerate(false)}>
                Annuler
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                icon={<DownloadOutlined />}
              >
                Régénérer et télécharger
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile;